<template>
  <div class="birthday-picker">
    <Select
      v-model="day"
      :options="dayOptions"
      :placeholder="t('loyaltyProgram.birthdayDay')"
      :aria-label="t('loyaltyProgram.birthdayDay')"
      class="bp-field"
      @update:modelValue="emitValue"
    />

    <Select
      v-model="month"
      :options="monthOptions"
      optionLabel="label"
      optionValue="value"
      :placeholder="t('loyaltyProgram.birthdayMonth')"
      :aria-label="t('loyaltyProgram.birthdayMonth')"
      class="bp-field bp-field--month"
      @update:modelValue="onMonthChange"
    />

    <Select
      v-model="year"
      :options="yearOptions"
      :placeholder="t('loyaltyProgram.birthdayYear')"
      :aria-label="t('loyaltyProgram.birthdayYear')"
      class="bp-field"
      :virtualScrollerOptions="{ itemSize: 38 }"
      @update:modelValue="onYearChange"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime, Info } from 'luxon';
import Select from 'primevue/select';

/**
 * Day / month / year selects for a date of birth.
 *
 * A calendar popup is the wrong control here — reaching 1988 means paging back some
 * three hundred months. Three selects land on any date in three taps.
 *
 * PrimeVue's Select rather than a native one: a bare `<select>` renders the platform's
 * own menu, which on desktop is a dark list that ignores the app's theme entirely.
 *
 * Emits a Date only once all three parts are chosen; a half-filled picker is worth
 * nothing to the caller, and the field is optional everywhere it is used.
 *
 * Shared between the booking site (where the customer gives their birthday when joining
 * the club) and the admin dashboard (where the owner fixes it afterwards), so the two
 * cannot drift into different controls for the same value.
 */
const props = defineProps<{ modelValue?: Date }>();
const emit = defineEmits<{ (e: 'update:modelValue', value: Date | undefined): void }>();

// The dashboard renders hardcoded Hebrew and installs no i18n, so fall back to it there
// rather than making the host wire up translations for three words.
const fallbackStrings: Record<string, string> = {
  'loyaltyProgram.birthdayDay': 'יום',
  'loyaltyProgram.birthdayMonth': 'חודש',
  'loyaltyProgram.birthdayYear': 'שנה',
};

let t: (key: string) => string;
let activeLocale: () => string;
try {
  const i18n = useI18n();
  t = i18n.t as (key: string) => string;
  activeLocale = () => i18n.locale.value;
} catch {
  t = (key: string) => fallbackStrings[key] ?? key;
  activeLocale = () => 'he';
}

const day = ref<number | undefined>();
const month = ref<number | undefined>();
const year = ref<number | undefined>();

const monthOptions = computed(() =>
  Info.months('long', { locale: activeLocale() }).map((label, i) => ({ label, value: i + 1 }))
);

// Nobody booking an appointment was born more than 110 years ago, and the list reads
// newest-first because that is where nearly every answer is.
const currentYear = DateTime.local().year;
const yearOptions = computed(() => Array.from({ length: 111 }, (_, i) => currentYear - i));

/** February is 28 or 29 days, and a year has to be picked before we can know which. */
const daysInMonth = computed(() => {
  if (!month.value) return 31;
  return DateTime.fromObject({ year: year.value ?? 2000, month: month.value }).daysInMonth ?? 31;
});

const dayOptions = computed(() => Array.from({ length: daysInMonth.value }, (_, i) => i + 1));

const emitValue = () => {
  if (!day.value || !month.value || !year.value) {
    emit('update:modelValue', undefined);
    return;
  }
  const date = DateTime.fromObject({ year: year.value, month: month.value, day: day.value });
  emit('update:modelValue', date.isValid ? date.toJSDate() : undefined);
};

/** Keep the day in range when the month or year shortens it (31 March → 30 April). */
const clampDay = () => {
  if (day.value && day.value > daysInMonth.value) day.value = daysInMonth.value;
};

const onMonthChange = () => {
  clampDay();
  emitValue();
};

const onYearChange = () => {
  clampDay();
  emitValue();
};

watch(
  () => props.modelValue,
  (value) => {
    if (!value) {
      day.value = undefined;
      month.value = undefined;
      year.value = undefined;
      return;
    }
    const date = DateTime.fromJSDate(value);
    if (!date.isValid) return;
    day.value = date.day;
    month.value = date.month;
    year.value = date.year;
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.birthday-picker {
  display: flex;
  gap: 8px;
  width: 100%;
}

.bp-field {
  flex: 1 1 0;
  min-width: 0;

  // The three read as one control, so they share the host's accent on focus. `--lead` is
  // the booking site's per-business colour; the dashboard has none and takes the fallback.
  :deep(.p-select) {
    width: 100%;
    border-radius: 12px;
  }

  :deep(.p-select:not(.p-disabled).p-focus) {
    border-color: var(--lead, #8864ee);
    box-shadow: 0 0 0 3px rgba(136, 100, 238, 0.18);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--lead, #8864ee) 18%, transparent);
  }

  :deep(.p-select-label) {
    padding: 12px 14px;
    font-size: 14.5px;
  }

  // "יום" / "חודש" / "שנה" are prompts, not answers, so they read as placeholder text.
  :deep(.p-select-label.p-placeholder) {
    color: #94a3b8;
  }
}

// The month name is the longest of the three, so it gets the extra room.
.bp-field--month {
  flex: 1.5 1 0;
}
</style>
